import { useEffect, useState } from 'react';

import { useDocumentById } from '@/hooks/useDocuments';
import { getAuditLogById } from '@/services/auditTrailService';

/**
 * Returns a unified document data source whether fetching from the API
 * or re-hydrating from a snapshot (audit-trail navigation).
 *
 * @param {Object} options
 * @param {string} options.id - Document UUID (ignored when isSnapshotMode is true)
 * @param {boolean} options.isSnapshotMode - When true, bypasses the live API fetch
 * @param {Object} options.snapshotData - Snapshot payload from location.state (legacy path)
 * @param {string} options.snapshotAuditLogId - Audit log id from ?snapshot= query param (server rehydrate path)
 * @param {Object[]} options.snapshotComments - Comments from location.state (legacy path)
 * @returns {Object} { document, isLoading, snapshotComments }
 */
export const useDocumentData = ({
  id,
  isSnapshotMode,
  snapshotData,
  snapshotAuditLogId,
  snapshotComments: legacySnapshotComments = [],
}) => {
  const {
    document,
    load: loadDocument,
    loading: isLoadingDocument,
  } = useDocumentById();

  // Server-rehydrated snapshot (from ?snapshot={auditLogId}) — refresh/share-safe
  const [serverSnapshot, setServerSnapshot] = useState(null);
  const [serverSnapshotComments, setServerSnapshotComments] = useState([]);
  const [snapshotFailed, setSnapshotFailed] = useState(false);

  useEffect(() => {
    if (isSnapshotMode && snapshotAuditLogId) {
      let cancelled = false;
      setServerSnapshot(null);
      setServerSnapshotComments([]);
      setSnapshotFailed(false);
      getAuditLogById(snapshotAuditLogId)
        .then((entry) => {
          if (!cancelled) {
            if (entry?.snapshot) {
              setServerSnapshot(entry.snapshot);
              setServerSnapshotComments(entry.comments || []);
            } else {
              setSnapshotFailed(true); // fall back to live doc below
            }
          }
        })
        .catch(() => {
          if (!cancelled) setSnapshotFailed(true); // fall back to live doc below
        });
      return () => {
        cancelled = true;
      };
    }
    setServerSnapshot(null);
    setServerSnapshotComments([]);
    setSnapshotFailed(false);
  }, [isSnapshotMode, snapshotAuditLogId]);

  useEffect(() => {
    if (!isSnapshotMode && id) {
      loadDocument(id);
    }
  }, [id, isSnapshotMode, loadDocument]);

  // Priority: server-rehydrated snapshot (?snapshot=) > location.state snapshot (legacy) > live document
  // On snapshot fetch failure, fall back to the live document (never a blank page)
  const snapshotOk = snapshotAuditLogId ? (!!serverSnapshot || snapshotFailed) : false;
  const displayDocument = isSnapshotMode
    ? (snapshotOk ? (serverSnapshot || null) : snapshotData)
    : document;

  // Comments: server-rehydrated (?snapshot=) > location.state (legacy) > none
  const snapshotComments = isSnapshotMode
    ? (snapshotAuditLogId ? serverSnapshotComments : legacySnapshotComments)
    : [];

  // Loading: only when fetching the live doc, or while the server snapshot loads
  const isLoading =
    (isSnapshotMode && snapshotAuditLogId && !serverSnapshot && !snapshotFailed) ||
    (!isSnapshotMode && !!id ? isLoadingDocument : false);

  return { document: displayDocument, isLoading, snapshotComments };
};
